import React, { Component, createRef } from "react";
import { ToastsStore } from "react-toasts";
import DocumentVerificationErrorTable from "./DocumentVerificationErrorTable";
import {
  AiProcessPromptConfig,
  AiVerificationContext,
  AiWorkflowStatus,
  compareProcessTypes,
  getAiWorkflowStatus,
  getAiVerificationPrompts,
  getCloseRemarkOptionsForProcess,
  isAiLatestRunMissing,
  isAiVerificationStatusInProgress,
  isAiVerificationScreenEnabled,
  isAiWorkFileScreenEnabled,
  formatAiVerificationErrorTitleMessage,
  isFileTakenForProcessType,
  pickLatestRunForProcess,
  requiresAiVerificationDocumentUpload,
  resolveProcessTypeForContext,
  runAiVerification,
  uploadAiVerificationDocument,
} from "../../utils/aiDocumentVerification";
import "./document-verification.css";

/** Set to 1 to run click validations. Set to 0 to skip them for testing. */
const validationenable: number = 1;

interface Props {
  fileId: string;
  filestatus: string;
  documentUrl: string;
  documentPath: string;
  localFileName: string;
  /** Word URL from work-file Upload Doc (`lastUploadedDocument.docx`). */
  workFileDocumentUrl?: string;
  /** True after the work-file Upload Doc file is selected/saved. */
  hasWorkFileUpload?: boolean;
  collapsed?: boolean;
  /** work-file = process/rectification by status; verification-list = verification when status != 5 */
  context?: AiVerificationContext;
  takenStatus?: number | string;
  vtakenStatus?: number | string;
  rtakenStatus?: number | string;
  blockDownloadStatus?: string | boolean;
  fileOpinionType?: string | number;
  onPendingChange?: (pendingCount: number, canComplete: boolean, latestRunAction?: string) => void;
  /** Same process-checklist gate used by the Complete button. */
  processChecklistStatus?: any;
  /** Extra Complete-button checks (restriction API / modal). Return false to block. */
  onBeforeRunVerification?: () => boolean | Promise<boolean>;
}

interface State {
  expanded: boolean;
  processType: string;
  running: boolean;
  refreshing: boolean;
  uploading: boolean;
  workflow: AiWorkflowStatus | null;
  settingsEnabled: boolean;
  lastRunInfo: string;
  processOptions: { key: string; label: string; model?: string }[];
  processPrompts: AiProcessPromptConfig[];
  errorTableReload: number;
  processUnavailable: boolean;
  uploadedDocumentPath: string;
  uploadedDocumentUrl: string;
  uploadedFileName: string;
  showRunLoader: boolean;
  runLoaderSeconds: number;
  noIssuesFoundDuringRun: boolean;
  runApiErrorMessage: string;
}

export default class DocumentVerificationPanel extends Component<Props, State> {
  /** Sync error panel open/closed from admin setting only once on first load. */
  private errorsVisibilitySynced = false;
  private documentUploadInputRef = createRef<HTMLInputElement>();
  private runLoaderTickId: any = null;
  private runLoaderPollId: any = null;
  private runIdAtLoaderStart: any = null;
  private workflowAutoRefreshId: any = null;

  state: State = {
    expanded: false,
    processType: "process",
    running: false,
    refreshing: false,
    uploading: false,
    workflow: null,
    settingsEnabled: false,
    lastRunInfo: "",
    processOptions: [],
    processPrompts: [],
    errorTableReload: 0,
    processUnavailable: false,
    uploadedDocumentPath: "",
    uploadedDocumentUrl: "",
    uploadedFileName: "",
    showRunLoader: false,
    runLoaderSeconds: 0,
    noIssuesFoundDuringRun: false,
    runApiErrorMessage: "",
  };

  componentDidMount() {
    this.syncProcessTypeFromFilestatus();
    this.restoreUploadedDocumentFromCache();
    this.loadWorkflow();
    this.loadProcessOptions();
  }

  getUploadCacheKey = (): string => `ai_verification_upload_${this.props.fileId}`;

  restoreUploadedDocumentFromCache = () => {
    try {
      const raw = sessionStorage.getItem(this.getUploadCacheKey());
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.document_path) {
        this.setState({
          uploadedDocumentPath: String(parsed.document_path).replace(/\\/g, "/"),
          uploadedFileName: parsed.file_name || "",
          uploadedDocumentUrl: "",
        });
      }
    } catch (_err) {
      // ignore invalid cache
    }
  };

  saveUploadedDocumentCache = (documentPath: string, fileName: string) => {
    sessionStorage.setItem(
      this.getUploadCacheKey(),
      JSON.stringify({
        document_path: documentPath,
        file_name: fileName,
      }),
    );
  };

  clearUploadedDocumentCache = () => {
    sessionStorage.removeItem(this.getUploadCacheKey());
  };

  parseUploadResponse = (response: any) => {
    const body =
      response && response.data && typeof response.data === "object" && response.success === undefined
        ? response.data
        : response;
    return {
      success: body?.success === true || body?.success === 1 || body?.success === "1",
      message: body?.message || "",
      document_path: String(body?.document_path || "").replace(/\\/g, "/"),
      file_name: body?.file_name || "",
    };
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.filestatus !== this.props.filestatus || prevProps.context !== this.props.context) {
      this.syncProcessTypeFromFilestatus();
      this.loadProcessOptions();
      this.loadWorkflow();
    }
    if (prevProps.fileId !== this.props.fileId) {
      this.stopWorkflowAutoRefresh();
      this.loadWorkflow();
      this.loadProcessOptions();
      this.clearUploadedDocumentCache();
      this.setState({
        uploadedDocumentPath: "",
        uploadedDocumentUrl: "",
        uploadedFileName: "",
      });
      this.restoreUploadedDocumentFromCache();
    }
  }

  componentWillUnmount() {
    this.stopRunLoader();
    this.stopWorkflowAutoRefresh();
  }

  usesDocumentUploadFlow = (): boolean =>
    requiresAiVerificationDocumentUpload(
      this.props.blockDownloadStatus,
      this.props.fileOpinionType,
    );

  showUploadFlowUi = (): boolean =>
    this.usesDocumentUploadFlow() || !!this.state.uploadedDocumentPath;

  getWorkFileDocumentUrl = (): string =>
    String(this.props.workFileDocumentUrl || "").trim();

  getActiveDocumentPath = (): string => {
    if (this.state.uploadedDocumentPath) {
      return this.state.uploadedDocumentPath;
    }
    return this.props.documentPath || this.getWorkFileDocumentUrl() || "";
  };

  getActiveDocumentUrl = (): string => {
    if (this.state.uploadedDocumentPath) {
      return this.state.uploadedDocumentUrl;
    }
    return this.getWorkFileDocumentUrl() || this.props.documentUrl || "";
  };

  getActiveFileName = (): string => {
    if (this.state.uploadedDocumentPath) {
      return this.state.uploadedFileName;
    }
    return this.props.localFileName || "";
  };

  hasDocumentReady = (): boolean => {
    if (this.usesDocumentUploadFlow()) {
      return !!this.props.hasWorkFileUpload;
    }
    if (this.state.uploadedDocumentPath) {
      return true;
    }
    return !!(this.props.documentPath || this.props.documentUrl);
  };

  triggerDocumentUpload = () => {
    this.documentUploadInputRef.current?.click();
  };

  removeUploadedDocument = () => {
    this.clearUploadedDocumentCache();
    this.setState({
      uploadedDocumentPath: "",
      uploadedDocumentUrl: "",
      uploadedFileName: "",
    });
    ToastsStore.success("Uploaded document removed.");
  };

  readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  handleDocumentUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (ext !== "doc" && ext !== "docx") {
      ToastsStore.error("Only Word documents (.doc, .docx) are allowed.");
      return;
    }

    const user: any = localStorage.getItem("user");
    const userId = user ? JSON.parse(user)["userId"] : "";

    this.setState({ uploading: true });
    try {
      const base64 = await this.readFileAsBase64(file);
      const response = await uploadAiVerificationDocument({
        file_idfk: this.props.fileId,
        file: base64,
        file_name: file.name,
        file_ext: ext,
        userId,
      });
      const parsed = this.parseUploadResponse(response);
      if (!parsed.success || !parsed.document_path) {
        throw new Error(parsed.message || "Upload failed");
      }
      this.saveUploadedDocumentCache(parsed.document_path, parsed.file_name || file.name);
      this.setState({
        uploading: false,
        uploadedDocumentPath: parsed.document_path,
        uploadedDocumentUrl: "",
        uploadedFileName: parsed.file_name || file.name,
        expanded: true,
      });
      ToastsStore.success("Document uploaded. You can now run verification.");
    } catch (error) {
      this.setState({ uploading: false });
      const message = error instanceof Error ? error.message : "Document upload failed";
      ToastsStore.error(message);
    }
  };

  getContext(): AiVerificationContext {
    return this.props.context || "work-file";
  }

  syncProcessTypeFromFilestatus = () => {
    const resolved = resolveProcessTypeForContext(
      this.getContext(),
      this.props.filestatus || "",
    );
    if (!resolved) {
      this.setState({ processUnavailable: true, processType: "" });
      return;
    }
    this.setState({ processType: resolved, processUnavailable: false });
  };

  getScopedPending(workflow: AiWorkflowStatus | null, processType: string): number {
    if (!workflow || !processType) return 0;
    const byProcess = workflow.pending_by_process || {};
    if (byProcess[processType] !== undefined) {
      return Number(byProcess[processType]) || 0;
    }
    return Number(workflow.total_pending) || 0;
  };

  getActiveProcessType = (): string => {
    return (
      resolveProcessTypeForContext(this.getContext(), this.props.filestatus || "") ||
      this.state.processType ||
      ""
    );
  };

  getLatestRunForActiveProcess = (workflow: AiWorkflowStatus | null) => {
    return pickLatestRunForProcess(workflow, this.getActiveProcessType());
  };

  formatLastRunInfo = (latestRun: any): string => {
    if (!latestRun || isAiLatestRunMissing(latestRun)) return "";
    return `Last run: ${latestRun.run_status} · ${latestRun.error_count || 0} errors · ${latestRun.created_at || ""}`;
  };

  notifyPendingChange = (workflow: AiWorkflowStatus | null) => {
    if (!this.props.onPendingChange) return;
    const processType = this.getActiveProcessType();
    const pending = this.getScopedPending(workflow, processType);
    const latestRun = this.getLatestRunForActiveProcess(workflow);
    this.props.onPendingChange(
      pending,
      pending === 0,
      String(latestRun?.action || ""),
    );
  };

  loadProcessOptions = () => {
    getAiVerificationPrompts()
      .then((response) => {
        if (response.success && Array.isArray(response.data)) {
          const processPrompts = response.data as AiProcessPromptConfig[];
          const activeType = this.state.processType;
          const processOptions = processPrompts
            .filter((p) => Number(p.is_enabled) === 1)
            .filter((p) => !activeType || p.process_type === activeType)
            .sort((a, b) => compareProcessTypes(a.process_type, b.process_type))
            .map((p) => ({
              key: p.process_type,
              label: p.process_label || p.prompt_title || p.process_type,
              model: p.openai_model,
            }));
          this.setState({ processOptions, processPrompts });
        }
      })
      .catch(() => {});
  };

  resolveUiEnabledFromWorkflow = (workflow: any): boolean => {
    if (this.getContext() === "verification-list") {
      return isAiVerificationScreenEnabled(workflow);
    }
    return isAiWorkFileScreenEnabled(workflow, this.props.filestatus);
  };

  applySettingsEnabled = (enabled: boolean) => {
    if (!this.errorsVisibilitySynced) {
      this.errorsVisibilitySynced = true;
      this.setState({ settingsEnabled: enabled, expanded: enabled });
      return;
    }
    if (!enabled) {
      this.setState({ settingsEnabled: enabled, expanded: false });
      return;
    }
    this.setState({ settingsEnabled: enabled });
  };

  isActionCompleted = (latestRun: any): boolean =>
    String(latestRun?.action || "").toLowerCase() === "completed";

  isNewRunSinceLoaderStart = (latestRun: any): boolean => {
    if (!latestRun || latestRun.id == null) return false;
    if (this.runIdAtLoaderStart == null || this.runIdAtLoaderStart === "") return true;
    return String(latestRun.id) !== String(this.runIdAtLoaderStart);
  };

  shouldShowNoIssuesFromRun = (latestRun: any): boolean =>
    this.isActionCompleted(latestRun) && this.isNewRunSinceLoaderStart(latestRun);

  loadWorkflow = () => {
    if (!this.props.fileId) return;
    const processType = this.getActiveProcessType();
    getAiWorkflowStatus(this.props.fileId, processType)
      .then((response) => {
        if (response.success && response.data) {
          const wf = response.data as AiWorkflowStatus;
          const latestRun = this.getLatestRunForActiveProcess(wf);
          this.applySettingsEnabled(this.resolveUiEnabledFromWorkflow(wf));
          this.setState((prev) => ({
            workflow: wf,
            lastRunInfo: this.formatLastRunInfo(latestRun),
            noIssuesFoundDuringRun:
              prev.showRunLoader && this.shouldShowNoIssuesFromRun(latestRun)
                ? true
                : prev.noIssuesFoundDuringRun,
          }));
          this.notifyPendingChange(wf);
          this.syncWorkflowAutoRefresh(latestRun);
        }
      })
      .catch(() => {});
  };

  startRunLoader = () => {
    this.stopRunLoader();
    const currentRun = this.getLatestRunForActiveProcess(this.state.workflow);
    this.runIdAtLoaderStart =
      currentRun?.id != null ? currentRun.id : null;
    this.setState({
      showRunLoader: true,
      runLoaderSeconds: 0,
      expanded: true,
      noIssuesFoundDuringRun: false,
    });
    this.runLoaderTickId = setInterval(() => {
      const next = this.state.runLoaderSeconds + 1;
      if (next >= 60) {
        this.setState({ runLoaderSeconds: 60 });
        if (this.runLoaderTickId) {
          clearInterval(this.runLoaderTickId);
          this.runLoaderTickId = null;
        }
        this.pollWorkflowDuringRun(true);
        return;
      }
      this.setState({ runLoaderSeconds: next });
    }, 1000);
    this.runLoaderPollId = setInterval(() => {
      this.pollWorkflowDuringRun();
    }, 10000);
  };

  stopRunLoader = () => {
    if (this.runLoaderTickId) {
      clearInterval(this.runLoaderTickId);
      this.runLoaderTickId = null;
    }
    if (this.runLoaderPollId) {
      clearInterval(this.runLoaderPollId);
      this.runLoaderPollId = null;
    }
    if (this.state.showRunLoader) {
      this.setState({ showRunLoader: false });
    }
  };

  pollWorkflowDuringRun = (stopIfNotCompleted?: boolean) => {
    if (!this.props.fileId) return;
    getAiWorkflowStatus(this.props.fileId, this.getActiveProcessType())
      .then((response) => {
        if (!response.success || !response.data) {
          if (stopIfNotCompleted) {
            this.stopRunLoader();
          }
          return;
        }
        const wf = response.data as AiWorkflowStatus;
        const latestRun = this.getLatestRunForActiveProcess(wf);
        const runStatus = String(latestRun?.run_status || "").toLowerCase();
        const actionCompleted = this.shouldShowNoIssuesFromRun(latestRun);
        const isNewRun = this.isNewRunSinceLoaderStart(latestRun);
        this.applySettingsEnabled(this.resolveUiEnabledFromWorkflow(wf));
        this.setState((prev) => ({
          workflow: wf,
          lastRunInfo: this.formatLastRunInfo(latestRun),
          noIssuesFoundDuringRun: actionCompleted ? true : prev.noIssuesFoundDuringRun,
          errorTableReload:
            actionCompleted || (isNewRun && (runStatus === "completed" || runStatus === "failed"))
              ? prev.errorTableReload + 1
              : prev.errorTableReload,
          expanded: true,
        }));
        this.notifyPendingChange(wf);
        this.syncWorkflowAutoRefresh(latestRun);

        if (isNewRun && runStatus === "failed") {
          this.stopRunLoader();
          return;
        }
        if (isNewRun && runStatus === "completed" && !actionCompleted) {
          this.stopRunLoader();
          return;
        }
        if (stopIfNotCompleted) {
          this.stopRunLoader();
        }
      })
      .catch(() => {
        if (stopIfNotCompleted) {
          this.stopRunLoader();
        }
      });
  };

  applyWorkflowRefresh = (wf: AiWorkflowStatus) => {
    const latestRun = this.getLatestRunForActiveProcess(wf);
    const lastRunInfo = this.formatLastRunInfo(latestRun);
    this.applySettingsEnabled(this.resolveUiEnabledFromWorkflow(wf));
    this.setState((prev) => ({
      refreshing: false,
      workflow: wf,
      lastRunInfo,
      errorTableReload: prev.errorTableReload + 1,
      expanded: true,
    }));
    this.notifyPendingChange(wf);
    this.syncWorkflowAutoRefresh(latestRun);
  };

  isLatestRunRunning = (latestRun?: any): boolean =>
    String(latestRun?.run_status || "").toLowerCase() === "running";

  syncWorkflowAutoRefresh = (latestRun?: any) => {
    if (this.isLatestRunRunning(latestRun)) {
      this.startWorkflowAutoRefresh();
      return;
    }
    this.stopWorkflowAutoRefresh();
  };

  startWorkflowAutoRefresh = () => {
    if (this.workflowAutoRefreshId || !this.props.fileId) return;
    this.workflowAutoRefreshId = setInterval(() => {
      if (!this.isLastRunRunning()) {
        this.stopWorkflowAutoRefresh();
        return;
      }
      this.refreshWorkflow(true);
    }, 30000);
  };

  stopWorkflowAutoRefresh = () => {
    if (this.workflowAutoRefreshId) {
      clearInterval(this.workflowAutoRefreshId);
      this.workflowAutoRefreshId = null;
    }
  };

  refreshWorkflow = (silent?: boolean) => {
    const isSilent = silent === true;
    if (!this.props.fileId || this.state.refreshing) return;
    if (!isSilent) {
      this.setState({ refreshing: true });
    }
    getAiWorkflowStatus(this.props.fileId, this.getActiveProcessType())
      .then((response) => {
        if (response.success && response.data) {
          this.applyWorkflowRefresh(response.data as AiWorkflowStatus);
          if (!isSilent) {
            ToastsStore.success("Verification data refreshed.");
          }
          return;
        }
        this.setState({ refreshing: false });
        if (!isSilent) {
          ToastsStore.error(response.message || "Failed to refresh verification data");
        }
      })
      .catch(() => {
        this.setState({ refreshing: false });
        if (!isSilent) {
          ToastsStore.error("Failed to refresh verification data");
        }
      });
  };

  handlePendingChange = (pendingCount: number, canComplete?: boolean) => {
      this.loadWorkflow();
    if (this.props.onPendingChange) {
      this.props.onPendingChange(
        pendingCount,
        canComplete !== undefined ? canComplete : pendingCount === 0,
      );
    }
  };

  isProcessChecklistMissing = (): boolean => this.props.processChecklistStatus == false;

  getProcessChecklistMessage = (): string =>
    "Save and submit the process checklist before running verification";

  getLastSuccessfulRunMessage = (): string =>
    "Run Verification cannot be execute because the last verification was successful with no errors.";

  isLastRunActionCompleted = (): boolean =>
    this.isActionCompleted(this.getLatestRunForActiveProcess(this.state.workflow));

  isLastRunRunning = (): boolean =>
    String(this.getLatestRunForActiveProcess(this.state.workflow)?.run_status || "").toLowerCase() === "running";

  getVerificationInProgressMessage = (): string =>
    "The recently executed verification is still in progress. Please wait for it to complete.";

  runVerification = async () => {
    if (this.props.onBeforeRunVerification) {
      try {
        const allowed = await this.props.onBeforeRunVerification();
        if (!allowed) return;
      } catch (_err) {
        ToastsStore.error("Unable to validate file restrictions. Please try again.");
        return;
      }
    }
    if (this.state.running || this.state.showRunLoader) return;
    if (this.isLastRunRunning()) {
      ToastsStore.success(this.getVerificationInProgressMessage());
      return;
    }
    if (validationenable == 1) {
    if (this.state.processUnavailable || !this.state.processType) {
      ToastsStore.error("System verification is not available for this file status.");
      return;
    }
    const openErrors = this.getScopedPending(this.state.workflow, this.state.processType);
    if (openErrors > 0) {
      ToastsStore.error("Complete the open error before execute Run Verification");
      return;
    }
    if (!this.props.onBeforeRunVerification && this.isProcessChecklistMissing()) {
      ToastsStore.error(this.getProcessChecklistMessage());
      return;
    }
    if (this.isLastRunActionCompleted()) {
      ToastsStore.success(this.getLastSuccessfulRunMessage());
      return;
    }
    if (!this.state.settingsEnabled) {
      ToastsStore.error("Enable system verification and configure API key in admin settings.");
      return;
    }
    }

    const user: any = localStorage.getItem("user");
    const userId = user ? JSON.parse(user)["userId"] : "";

    this.setState({ running: true, runApiErrorMessage: "" });
    this.startRunLoader();
    runAiVerification({
      file_idfk: this.props.fileId,
      process_type: this.state.processType,
      document_url: this.getActiveDocumentUrl(),
      document_path: this.getActiveDocumentPath(),
      file_name: this.getActiveFileName(),
      userId,
    })
      .then((response) => {
        this.setState({ running: false });
        if (response.success) {
         /* ToastsStore.success(
            `Verification completed. ${response.error_count || 0} error(s) found. Tokens: ${response.token_usage?.total_tokens || 0}`,
          );*/
          this.setState((prev) => ({
            lastRunInfo: `Last run: completed · ${response.error_count || 0} errors · ${response.response_time_ms || 0}ms`,
            errorTableReload: prev.errorTableReload + 1,
            expanded: true,
            runApiErrorMessage: "",
          }));
          this.loadWorkflow();
        } else {
          this.stopRunLoader();
          const titledError = formatAiVerificationErrorTitleMessage(response);
          if (titledError) {
            this.setState({ expanded: true, runApiErrorMessage: titledError });
            return;
          }
          ToastsStore.error(response.message || "Verification failed");
        }
      })
      .catch(() => {
        this.setState({ running: false });
        this.stopRunLoader();
        ToastsStore.error("Verification request failed");
      });
  };

  isFileTakenForActiveProcess = (): boolean => {
    const { processType } = this.state;
    return isFileTakenForProcessType(
      processType,
      this.props.takenStatus,
      this.props.vtakenStatus,
      this.props.rtakenStatus,
    );
  };

  getFileNotTakenMessage = (): string => {
    const { processType } = this.state;
    if (processType === "verification") {
      return "File must be taken in verification before running verification.";
    }
    if (processType === "rectification") {
      return "File must be taken in rectification before running verification.";
    }
    return "File must be taken in process before running verification.";
  };

  getRunVerificationDisabledMessage = (_params: {
    settingsEnabled: boolean;
    fileTaken: boolean;
    running: boolean;
    uploading: boolean;
    hasOpenErrors: boolean;
    documentReady: boolean;
    usesUploadFlow: boolean;
  }): string | null => {
    return null;
  };

  render() {
    const {
      expanded,
      processType,
      running,
      refreshing,
      uploading,
      workflow,
      settingsEnabled,
      lastRunInfo,
      processUnavailable,
      uploadedFileName,
      showRunLoader,
      runLoaderSeconds,
      noIssuesFoundDuringRun,
      runApiErrorMessage,
    } = this.state;
    const context = this.getContext();
    const scopedPending = this.getScopedPending(workflow, processType);
    const latestRun = this.getLatestRunForActiveProcess(workflow);
    const canComplete =
      scopedPending === 0 &&
      !(workflow && isAiLatestRunMissing(latestRun)) &&
      !isAiVerificationStatusInProgress(latestRun);
    const hasOpenErrors = scopedPending > 0;
    const isResubmitRequiredAllClosed =
      String(latestRun?.action || "").toLowerCase() === "resubmit_required" &&
      scopedPending === 0;
    const fileTaken = this.isFileTakenForActiveProcess();
    const usesUploadFlow = this.showUploadFlowUi();
    const documentReady = this.hasDocumentReady();
    const hasUploadedDocument = !!this.state.uploadedDocumentPath;

    const processLabel =
      this.state.processOptions.find((p) => p.key === processType)?.label || processType;
    const runVerificationDisabledMessage = this.getRunVerificationDisabledMessage({
      settingsEnabled,
      fileTaken,
      running,
      uploading,
      hasOpenErrors,
      documentReady,
      usesUploadFlow,
    });

    if (!settingsEnabled) {
      return null;
    }

    if (processUnavailable) {
      return (
        <div className="ai-verification-panel">
          <div className="alert alert-secondary mb-0">
            {context === "verification-list"
              ? "System verification is not available — file verification is already completed (status 5)."
              : "System verification is not available for this file status (status 6)."}
          </div>
        </div>
      );
    }

    return (
      <div className="ai-verification-panel">
        <div className="ai-verification-header">
          <div>
            <h5>System Document Verification</h5>
          </div>
          <div className="ai-verification-header-actions">
            {runVerificationDisabledMessage ? (
              <span className="ai-action-block-msg" role="status">
                {runVerificationDisabledMessage}
              </span>
            ) : null}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={running || uploading || showRunLoader}
              onClick={this.runVerification}
              title={
                runVerificationDisabledMessage ||
                (documentReady ? "Run verification on the uploaded document" : "")
              }
            >
              {running || showRunLoader ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Verifying...
                </>
              ) : (
                "Run Verification"
              )}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={refreshing || showRunLoader}
              onClick={() => this.refreshWorkflow()}
              title="Refresh latest verification data"
            >
              {refreshing ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Refreshing...
                </>
              ) : (
                <>
                  <i className="fa fa-refresh" /> Refresh
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => this.setState({ expanded: !expanded })}
              disabled={!settingsEnabled || showRunLoader}
              title={
                !settingsEnabled
                  ? "Enable System Document Verification in admin settings to view errors"
                  : undefined
              }
            >
              {expanded ? "Hide Errors" : "Show Errors"}
            </button>
          </div>
        </div>

        {settingsEnabled && expanded && (
          <div className="ai-verification-body">
            {showRunLoader ? (
              <div className="text-center ai-verification-run-loader">
                <h4>
                  <span>Loading... {runLoaderSeconds} seconds</span>
                </h4>
                {noIssuesFoundDuringRun ? (
                  <div className="ai-error-summary-success">
                    No issues found. Proceed to complete the file.
                  </div>
                ) : this.isLastRunRunning() ? (
                  <div className="ai-error-summary-progress">
                     System Verification in Progress, Please wait.....
                  </div>
                ) : isResubmitRequiredAllClosed ? (
                  <div className="ai-error-summary-success">
                    All issues have been resolved. Proceed with verification again.
                  </div>
                ) : null}
              </div>
            ) : null}
            {!showRunLoader ? (
            <div className="ai-verification-run-results">
            <div className="ai-verification-status-row">
              <div className="ai-verification-stat ai-verification-stat--inline ai-verification-stat--danger">
                <span className="ai-verification-stat-label">
                  Open Errors ({processLabel || processType})
                </span>
                <span className="ai-verification-stat-separator"> - </span>
                <strong className="ai-verification-stat-value">{scopedPending}</strong>
              </div>
              <div
                className={`ai-verification-stat ai-verification-stat--inline ai-verification-stat--${
                  canComplete ? "success" : "danger"
                }`}
              >
                <span className="ai-verification-stat-label">Can Complete File</span>
                <span className="ai-verification-stat-separator"> - </span>
                <strong className="ai-verification-stat-value">{canComplete ? "Yes" : "No"}</strong>
              </div>
            </div>

            {usesUploadFlow && uploadedFileName && hasUploadedDocument ? (
              <div className="ai-verification-upload-note">
                <i className="fa fa-check-circle" aria-hidden="true" />
                <span>
                  Ready for verification: <strong>{uploadedFileName}</strong>
                </span>
              </div>
            ) : null}

            {lastRunInfo && (
              <div className="ai-verification-last-run">
                <i className="fa fa-clock-o" aria-hidden="true" />
                <span>{lastRunInfo}</span>
              </div>
            )}

            {hasOpenErrors && (
              <div className="alert alert-warning py-2">
                Complete and Run Verification stay disabled until all {scopedPending} open error(s) for{" "}
                <strong>{processLabel || processType}</strong> are closed with a remark (
                {getCloseRemarkOptionsForProcess(this.state.processPrompts, processType).join(", ")}
                ).
              </div>
            )}

            <DocumentVerificationErrorTable
              fileId={this.props.fileId}
              processType={processType}
              processPrompts={this.state.processPrompts}
              reloadToken={this.state.errorTableReload}
              latestRun={latestRun}
              apiErrorMessage={runApiErrorMessage}
              onPendingChange={this.handlePendingChange}
            />
            </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }
}
